import React, { useCallback, useEffect, useState, useRef } from "react";
import { FaUser } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { HiDotsVertical } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import backIcon from "../../assets/images/arrow-left.svg";
import { getReviews, sendReview } from "./service";
import { errorMessage } from "../../utils/helper";
import useStore from "../../store/useStore";
import ReadMore from "./components/ReadMore";

interface Review {
  id: number;
  userId: number;
  name: string;
  profilePhotoPath: string;
  reviewText: string;
  created: string;
  editable: boolean;
}

export default function CommentThread() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { contentDetails } = state != null && state;

  const { tatvaPediaToken } = useStore();
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");
  const token = getToken();
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [text, setText] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const fetchReviews = async () => {
    const res: any = await getReviews(
      contentDetails?.id,
      contentDetails?.contentTypeId,
      token
    );
    if (res !== undefined) {
      setReviews(res?.dtoList);
    } else if (res === undefined) {
      navigate(-1);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Handle mobile keyboard visibility using Visual Viewport API
  useEffect(() => {
    const handleViewportChange = () => {
      // Only apply on mobile devices
      if (window.innerWidth <= 768 && window.visualViewport && window.visualViewport !== null) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        
        // If keyboard height is significant, consider it open
        if (keyboardHeight > 150) {
          setKeyboardVisible(true);
        } else {
          setKeyboardVisible(false);
        }
      }
    };

    // Only add listener on mobile devices
    if (window.innerWidth <= 768 && window.visualViewport && window.visualViewport !== null) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    }
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      // Force re-render on iOS WebView
      if (inputRef.current) {
        inputRef.current.style.opacity = "0.99";
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.style.opacity = "1";
          }
        }, 10);
      }
    },
    []
  );

  const handleFocus = () => {
    setIsFocused(true);
    setKeyboardVisible(true);
    
    // Scroll input into view when keyboard opens (only on mobile)
    if (window.innerWidth <= 768) { // Only on mobile devices
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Force cursor to end of input
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
          
          // Scroll the input into view
          inputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300); // Increased delay to allow keyboard to fully open
    } else {
      // Desktop behavior - just focus without scrolling
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setKeyboardVisible(false);
  };

  const onReviewSubmit = async () => {
    if (text) {
      const res: any = await sendReview(
        contentDetails?.id,
        contentDetails?.contentTypeId,
        text,
        token
      );
      if (res !== undefined && res.status) {
        setText("");
        fetchReviews();
      }
    } else {
      errorMessage("Enter comment", "warning");
    }
  };

  return (
    <div>
      <div className="sticky top-0 bg-white z-10">
        <div className="flex gap-1.5 items-center px-3 py-4 text-base font-semibold tracking-tight text-center bg-white border-b border-solid border-b-zinc-200 text-[#1F2933]">
          <img
            loading="lazy"
            src={backIcon}
            className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
            onClick={() => navigate(-1)}
          />
          All comments
        </div>
      </div>
      <div
        className="flex flex-col overflow-y-auto"
        style={{ 
          height: keyboardVisible ? "calc(100vh - 57px - 250px)" : "calc(100vh - 57px)",
          transition: "height 0.3s ease"
        }}
      >
        <div className="flex-auto p-5 text-left overflow-y-auto">
          {reviews?.map((item: any, index: number) => (
            <div key={item?.id} className="flex py-3">
              {item?.profilePhotoPath ? (
                <img
                  loading="lazy"
                  src={item?.profilePhotoPath}
                  className="max-h-8 max-w-8 rounded-3xl"
                />
              ) : (
                <FaUser className="bg-[#F1F1F5] min-h-8 min-w-8 p-2 rounded-3xl text-[#5A6774]" />
              )}
              <div className="ml-2 w-full">
                <div className="flex justify-between">
                  <div>
                    <div className="text-[#222222] font-bold text-[16px]">
                      {item?.name}
                    </div>
                    <div className="text-[12px] font-normal text-[#606060]">
                      {moment
                        .utc(item?.created, "YYYY-MM-DD HH:mm:ss")
                        .local()
                        .fromNow()}
                    </div>
                  </div>
                  {/* <HiDotsVertical className="min-w-5 min-h-5" /> */}
                </div>
                <ReadMore>{item?.reviewText}</ReadMore>
              </div>
            </div>
          ))}
        </div>

        <div className={`py-2 mx-5 ${keyboardVisible ? 'sticky bottom-0 bg-white z-10 shadow-lg' : ''}`} style={{ 
          paddingBottom: keyboardVisible ? '20px' : '8px',
          marginBottom: keyboardVisible ? '10px' : '0'
        }}>
          <div className="flex overoverflow-hidden border rounded-[10px] border-primary-100 border-[#D0D5DD]">
            <input
              type="text"
              value={text}
              ref={inputRef}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleInputChange}
              placeholder="Write your comment"
              className="flex-auto w-full px-3 py-3 outline-none rounded-[10px] transition placeholder:text-[#98A2B3] placeholder:text-[14px] bg-white"
              style={{
                // iOS WebView specific fixes
                WebkitAppearance: "none",
                WebkitUserSelect: "text",
                userSelect: "text",
                transform: "translateZ(0)",
                WebkitTransform: "translateZ(0)",
                willChange: "contents",
                // Force re-render when focused to show cursor
                opacity: isFocused ? 0.99 : 1,
                // Ensure text color is visible
                color: text ? "#475569" : "#94a3b8",
              }}
              // iOS specific attributes
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
            <button
              className="px-3 uppercase leading-normal text-2xl text-primary"
              onClick={onReviewSubmit}
            >
              <IoMdSend />
            </button>
          </div>
        </div>
        {/* <div className="p-5 text-left">

          <div className="text-[#454551] text-[14px] font-light">Reply to <span className="font-bold">Sophey's</span> Comment</div>

          <div className="mt-2">
            <CommentSection />
          </div>

          <div className="flex items-center justify-between py-2">
            <div className="relative flex w-full flex-wrap border rounded-[10px] border-primary-100 border-[#D0D5DD]">
              <input type="text" className="block flex-auto px-3 py-3 outline-none rounded-[10px] transition placeholder:text-[#98A2B3] placeholder:text-[14px]" placeholder="Write your review" />
              <button className="px-3 uppercase leading-normal text-2xl text-primary">
                <IoMdSend />
              </button>
            </div>
          </div>

        </div> */}
      </div>
    </div>
  );
}
